// [UNIFIED GRID COMPONENT - MAR 2026]: Merged GridComponent + GridComponentSimple
// [PURPOSE]: Single grid renderer supporting both drag-drop and tap-to-move modes
// [WHY]: Eliminates code duplication between two nearly-identical components
// [INTEGRATION]: Used by App.jsx, ContainerModal, VaultModal — mode controlled via enableDragDrop prop
import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { Package, Info } from 'lucide-react';
import { useInventory } from '../contexts/InventoryContext';
import { useUI } from '../contexts/UIContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumAccess } from '../hooks/usePremiumAccess';
import { systemBus, EVENTS } from '../services/systemBus';
import { effectCalculator } from '../services/effectCalculator';
import useIsMobile from '../hooks/useIsMobile';
import { getItemDisplayName } from '../utils/itemIdentificationGate'; // [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Mask name for unidentified items

const GridComponent = React.memo(({
  gridId,
  cols,
  rows,
  items,
  title,
  // Tap-to-move state (required for item selection)
  selectedItem,
  setSelectedItem,
  selectedItemSource,
  setSelectedItemSource,
  // Full-mode props (optional — omit for tap-only mode)
  isValidPosition,
  handleMouseDown,
  handleContextMenu,
  handleTouchMove,
  handleCellClick,
  setOpenContainer,
  setEditingItem,
  InventoryItem,
  // Feature flags
  enableDragDrop = true,      // false = tap-only mode (no drag hover, internal move logic)
  showItemDetails = true,     // false = icon+name only (no weight, attunement, custom images)
}) => {
  // ============================================================================
  // CONTEXT HOOKS
  // ============================================================================
  const {
    draggedItem,
    hoveredGrid,
    hoveredCell,
    moveItem,
  } = useInventory();
  const { showStatus } = useUI();
  const { settings } = useSettings();
  const { isPremium: authIsPremium } = useAuth(); // [LEGACY]
  const hasPremium = usePremiumAccess();
  const isMobile = useIsMobile();

  // [PURPOSE]: Force re-render when inventory events occur
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // Touch interaction state
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);

  // ============================================================================
  // EVENT SUBSCRIPTIONS — unified from GridComponent + GridComponentSimple
  // ============================================================================
  // [PURPOSE]: Re-render grid when inventory state changes
  // [WHY]: Ensures weight displays, item counts, and grid layout stay in sync
  // [INTEGRATION]: Always clears effectCalculator cache for accurate stat recalculation
  useEffect(() => {
    const handleInventoryChange = () => {
      effectCalculator.clearCache();
      forceUpdate();
    };

    const unsubs = [
      systemBus.on(EVENTS.INVENTORY_ITEM_ADDED, handleInventoryChange),
      systemBus.on(EVENTS.INVENTORY_ITEM_REMOVED, handleInventoryChange),
      systemBus.on(EVENTS.INVENTORY_ITEM_MOVED, handleInventoryChange),
      systemBus.on(EVENTS.ITEM_EQUIPPED, handleInventoryChange),
      systemBus.on(EVENTS.ITEM_UNEQUIPPED, handleInventoryChange),
      systemBus.on(EVENTS.INVENTORY_VAULT_TRANSFER, handleInventoryChange),
      systemBus.on(EVENTS.EQUIPMENT_CHANGED, handleInventoryChange),
    ];

    return () => unsubs.forEach(unsub => unsub());
  }, [gridId]);

  // ============================================================================
  // CELL CONTENT HELPERS
  // ============================================================================
  const getCellContent = useCallback((x, y) => {
    return items.find(item => {
      if (!item.position) return false;
      const [itemX, itemY] = item.position;
      return itemX === x && itemY === y;
    });
  }, [items]);

  // [FIX MAR 2026]: Check if ANY part of an item occupies this cell (not just top-left corner)
  // Used by tap-mode to determine if a cell is truly empty
  const getCellOccupant = useCallback((x, y) => {
    return items.find(item => {
      if (!item.position || !item.size) return false;
      const [itemX, itemY] = item.position;
      const [itemW, itemH] = item.size;
      return x >= itemX && x < itemX + itemW && y >= itemY && y < itemY + itemH;
    });
  }, [items]);

  const isOverContainer = (x, y) => {
    if (!draggedItem) return false;
    return items.find(item => {
      if (!item.isContainer || !item.position) return false;
      const [itemX, itemY] = item.position;
      return x >= itemX && x < itemX + item.size[0] &&
             y >= itemY && y < itemY + item.size[1];
    });
  };

  // ============================================================================
  // TAP MODE HANDLERS (used when enableDragDrop is false)
  // ============================================================================
  // [PURPOSE]: Internal item selection and movement for tap-only mode
  // [WHY]: Replaces external handleCellClick with direct moveItem call
  const handleItemClick = useCallback((e, item) => {
    e.stopPropagation();
    e.preventDefault();
    if (selectedItem?.id === item.id && selectedItemSource === gridId) {
      setSelectedItem(null);
      setSelectedItemSource(null);
      showStatus('Item deselected');
    } else {
      setSelectedItem(item);
      setSelectedItemSource(gridId);
      showStatus(`Selected: ${item.name}. Tap an empty cell to move it.`);
    }
  }, [selectedItem, selectedItemSource, gridId, setSelectedItem, setSelectedItemSource, showStatus]);

  // [FIX MAR 2026]: Unified tap handler for both empty cells and occupied cells
  // Handles: selecting items, deselecting, and moving to empty cells
  const handleTapCell = useCallback((x, y) => {
    // Check if tapping on an item (including multi-cell items)
    const occupant = getCellOccupant(x, y);

    if (occupant) {
      // Tapping on an item — select or deselect it
      if (selectedItem?.id === occupant.id && selectedItemSource === gridId) {
        setSelectedItem(null);
        setSelectedItemSource(null);
        showStatus('Item deselected');
      } else {
        setSelectedItem(occupant);
        setSelectedItemSource(gridId);
        showStatus(`Selected: ${occupant.name}. Tap an empty cell to move it.`);
      }
      return;
    }

    // Tapping on an empty cell — move selected item there
    if (!selectedItem) return;

    if (isValidPosition && !isValidPosition(selectedItem, x, y, gridId, selectedItem.id)) {
      showStatus('Cannot place item there!');
      return;
    }

    moveItem(selectedItem.id, [x, y]);
    setSelectedItem(null);
    setSelectedItemSource(null);
    showStatus(`Moved ${selectedItem.name}!`);
  }, [selectedItem, selectedItemSource, gridId, moveItem, isValidPosition, getCellOccupant, setSelectedItem, setSelectedItemSource, showStatus]);

  // ============================================================================
  // CELL CLASS — visual feedback for drag hover and tap selection
  // ============================================================================
  const getCellClass = (x, y) => {
    let baseClass = "w-12 h-12 border border-gray-600 relative transition-colors ";

    // Tap mode (no drag): green pulse on ALL empty cells when item selected
    if (!enableDragDrop && selectedItem && !getCellOccupant(x, y)) {
      baseClass += "bg-green-900/30 border-green-400 animate-pulse cursor-pointer ";
      return baseClass;
    }

    // Drag mode: show valid drop zones on mobile when item selected
    if (enableDragDrop && isMobile && selectedItem && !getCellContent(x, y)) {
      if (isValidPosition && isValidPosition(selectedItem, x, y, gridId, selectedItem.id)) {
        baseClass += "bg-green-900 border-green-400 animate-pulse ";
        return baseClass;
      }
    }

    // Drag mode: hover feedback during drag operation
    if (enableDragDrop && draggedItem && hoveredGrid === gridId && hoveredCell) {
      const containerAtCell = isOverContainer(x, y);
      if (containerAtCell && gridId === 'inventory') {
        baseClass += "bg-blue-700 border-blue-400 ";
      } else if (x >= hoveredCell.x &&
                 y >= hoveredCell.y &&
                 x < hoveredCell.x + draggedItem.size[0] &&
                 y < hoveredCell.y + draggedItem.size[1]) {
        if (isValidPosition(draggedItem, hoveredCell.x, hoveredCell.y, gridId, draggedItem.id)) {
          baseClass += "bg-green-700 ";
        } else {
          baseClass += "bg-red-800 ";
        }
      } else {
        baseClass += "bg-gray-800 ";
      }
    } else {
      baseClass += "bg-gray-800 ";
    }

    return baseClass;
  };

  // ============================================================================
  // TOUCH HANDLERS
  // ============================================================================
  const handleTouchStart = (e, x, y, item = null) => {
    if (!isMobile) return;
    setTouchStartTime(Date.now());
    setTouchStartPos({ x, y });
    if (longPressTimer) clearTimeout(longPressTimer);

    // Long press -> context menu
    const timer = setTimeout(() => {
      if (item && handleContextMenu) {
        if (navigator.vibrate) navigator.vibrate(50);
        handleContextMenu(e, item, gridId);
      }
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = (e, x, y, item = null) => {
    if (!isMobile) return;
    const touchDuration = Date.now() - touchStartTime;
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    // Short tap (not long press)
    if (touchDuration < 500) {
      if (!enableDragDrop) {
        // Tap-only mode: let onClick handle all taps via handleTapCell
        // Do NOT preventDefault — browser must synthesize click event
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Drag mode: use external handlers for tap interactions
      const useTapMode = settings?.mobileInteractionMode === 'tap';
      if (useTapMode) {
        if (item) {
          if (selectedItem?.id === item.id) {
            setSelectedItem(null);
            setSelectedItemSource(null);
            showStatus('Deselected');
          } else {
            setSelectedItem(item);
            setSelectedItemSource(gridId);
            showStatus(`Selected: ${item.name}. Tap empty cell to move`);
          }
        } else if (selectedItem && handleCellClick) {
          handleCellClick(x, y, gridId);
        }
      }
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="bg-gray-800 p-4 rounded-lg">
      <h3 className="text-lg font-bold text-white mb-3">{title}</h3>

      <div
        className="relative inline-block bg-gray-900 rounded"
        style={{ padding: '8px' }}
        onContextMenu={e => e.preventDefault()}
        data-grid-container="true"
        data-grid-id={gridId}
        data-grid-cols={cols}
        data-grid-rows={rows}
      >
        {/* Empty state overlay */}
        {(!items || items.length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 1 }}>
            <div className="text-center">
              <Package size={36} className="text-gray-700 mx-auto mb-2" />
              <p className="text-gray-600 text-sm font-medium">Empty</p>
            </div>
          </div>
        )}

        <div
          className="inline-grid gap-0"
          style={{ gridTemplateColumns: `repeat(${cols}, 48px)` }}
        >
          {Array.from({ length: rows }, (_, y) =>
            Array.from({ length: cols }, (_, x) => {
              const item = getCellContent(x, y);

              return (
                <div
                  key={`${x}-${y}`}
                  className={getCellClass(x, y)}
                  onTouchStart={(e) => handleTouchStart(e, x, y)}
                  onTouchEnd={(e) => handleTouchEnd(e, x, y)}
                  onClick={!enableDragDrop ? (e) => {
                    e.stopPropagation();
                    handleTapCell(x, y);
                  } : undefined}
                >
                  {item && (
                    <div
                      className={`group absolute border-2 rounded flex items-center justify-center text-xs text-white font-bold p-1 transition-all z-10 select-none ${
                        selectedItem?.id === item.id && selectedItemSource === gridId
                          ? 'bg-blue-700 border-blue-400 shadow-lg shadow-blue-500/50 ring-2 ring-blue-400 ring-opacity-75 scale-105 animate-pulse'
                          : 'bg-amber-700 border-amber-900 hover:bg-amber-600'
                      } ${enableDragDrop ? (isMobile ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing') : 'cursor-pointer'}`}
                      data-is-item="true"
                      data-testid={`grid-item-${item.id}`}
                      style={{
                        width: `${item.size[0] * 48}px`,
                        height: `${item.size[1] * 48}px`,
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none'
                      }}
                      title={(() => {
                        // [STACK INVESTIGATION APR 30 2026]: Tooltip surfaces quantity for
                        //   stacks so the player can see at-a-glance how many items occupy
                        //   the tile. Falls back to container drop-hint or item name + weight.
                        if (enableDragDrop && item.isContainer && draggedItem) {
                          return `Drop here to add to ${item.name}`;
                        }
                        const parts = [item.name];
                        if (item.stackable && (item.quantity || 0) > 1) {
                          parts.push(`Quantity: ${item.quantity}`);
                        }
                        if (item.weight) {
                          parts.push(`${item.weight} lb each`);
                        }
                        return parts.join(' • ');
                      })()}
                      // Desktop mouse handlers — only in drag mode
                      onMouseDown={enableDragDrop && handleMouseDown ? (e) => {
                        if (!isMobile) handleMouseDown(e, item, gridId);
                      } : undefined}
                      onContextMenu={handleContextMenu ? (e) => handleContextMenu(e, item, gridId) : undefined}
                      // Touch handlers — disabled in tap-only mode (let cell handle via onClick)
                      onTouchStart={enableDragDrop ? (e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        const useDragMode = settings?.mobileInteractionMode === 'drag';
                        if (useDragMode && item && handleMouseDown) {
                          // Drag mode: set up drag threshold
                          const touch = e.touches[0];
                          const startX = touch.clientX;
                          const startY = touch.clientY;
                          let dragStarted = false;

                          const handleTouchMoveThreshold = (moveEvent) => {
                            if (dragStarted) return;
                            const currentTouch = moveEvent.touches[0];
                            const deltaX = Math.abs(currentTouch.clientX - startX);
                            const deltaY = Math.abs(currentTouch.clientY - startY);
                            if (deltaX > 5 || deltaY > 5) {
                              dragStarted = true;
                              moveEvent.preventDefault();
                              e.target.removeEventListener('touchmove', handleTouchMoveThreshold);
                              e.target.removeEventListener('touchend', handleTouchEndThreshold);
                              const mouseEvent = {
                                button: 0,
                                clientX: currentTouch.clientX,
                                clientY: currentTouch.clientY,
                                preventDefault: () => {},
                                stopPropagation: () => {}
                              };
                              handleMouseDown(mouseEvent, item, gridId);
                            }
                          };
                          const handleTouchEndThreshold = () => {
                            e.target.removeEventListener('touchmove', handleTouchMoveThreshold);
                            e.target.removeEventListener('touchend', handleTouchEndThreshold);
                          };
                          e.target.addEventListener('touchmove', handleTouchMoveThreshold, { passive: false });
                          e.target.addEventListener('touchend', handleTouchEndThreshold);
                        } else {
                          // Tap mode within drag-enabled component
                          handleTouchStart(e, x, y, item);
                        }
                      } : undefined}
                      onTouchEnd={enableDragDrop ? (e) => {
                        e.stopPropagation();
                        handleTouchEnd(e, x, y, item);
                      } : undefined}
                      // Click handlers — in non-drag mode, let clicks bubble to cell's handleTapCell
                      onClick={undefined}
                      onDoubleClick={enableDragDrop && (setOpenContainer || setEditingItem) ? () => {
                        if (item.isContainer) {
                          if (gridId === 'inventory' && setOpenContainer) {
                            setOpenContainer(item);
                          } else if (gridId.startsWith('container_')) {
                            showStatus("Containers inside other containers cannot be opened. Move it to your main inventory first.");
                          } else {
                            showStatus("Containers in vaults cannot be opened. Move it to your main inventory first.");
                          }
                        } else if (setEditingItem) {
                          setEditingItem({ item, source: gridId });
                        }
                      } : undefined}
                    >
                      {/* [STACK INVESTIGATION APR 30 2026]: Quantity badge for stackable items.
                          Visible across all 3 render modes (custom image, detailed, simple) so
                          the player can see "I have 6 of these" from any inventory surface. The
                          gating on stackable + quantity > 1 keeps non-stack items visually
                          uncluttered. Bottom-right pill, white-on-dark for high contrast. */}
                      {item.stackable && (item.quantity || 0) > 1 && (
                        <div
                          data-testid={`grid-quantity-badge-${item.id}`}
                          className="absolute bottom-0 right-0 bg-gray-900 text-white text-[10px] font-bold rounded-tl-md rounded-br px-1.5 py-0.5 leading-none shadow-md border border-gray-700 pointer-events-none"
                          style={{ zIndex: 20, minWidth: '18px', textAlign: 'center' }}
                        >
                          {item.quantity}
                        </div>
                      )}
                      {/* [G2 FIX]: Info button — shows on hover (desktop) or always (mobile)
                          [F-008 ext 2026-05-09]: bumped mobile opacity to 100% with bg/border so the
                          icon registers as a tappable affordance over varied item icons. Was opacity-70
                          which made it visually de-emphasized; new look has consistent dark pill background. */}
                      {setEditingItem && (
                        <button
                          className={`absolute top-0.5 right-0.5 z-20 rounded p-0.5 bg-black/70 border border-gray-500/40 text-gray-200 hover:text-white hover:bg-black/85 transition-colors ${
                            isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          }`}
                          style={{ lineHeight: 0 }}
                          title={`View ${item.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem({ item, source: gridId });
                          }}
                        >
                          <Info size={10} />
                        </button>
                      )}
                      {/* Item content rendering — 3 modes: custom image, detailed, simple */}
                      {showItemDetails && item.customImage && hasPremium ? (
                        // [D.7 APR 2026]: Use object-cover + zero padding so AI-generated
                        // images fill their cell instead of letterboxing inside padding.
                        <div className="w-full h-full flex items-center justify-center">
                          <img
                            src={item.customImage}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            style={{ aspectRatio: '1 / 1', imageRendering: 'auto' }}
                          />
                        </div>
                      ) : showItemDetails ? (
                        // [D.7 APR 2026]: flex-col with gap + justify-center keeps icon
                        // from floating above the cell when name/weight push it upward.
                        <div className="h-full flex flex-col items-center justify-center gap-0.5 pointer-events-none text-center">
                          <div className="flex items-center justify-center gap-0.5" style={{ flexShrink: 0 }}>
                            <span className="text-2xl">{item.icon}</span>
                            {item.attuned && (
                              <span className="text-purple-400 text-xs" title="Attuned" style={{ marginTop: '-8px' }}>
                                {'\uD83D\uDD2E'}
                              </span>
                            )}
                            {item.requiresAttunement && !item.attuned && (
                              <span className="text-red-400 text-xs" title="Requires Attunement (not attuned)" style={{ marginTop: '-8px' }}>
                                {'\uD83D\uDD12'}
                              </span>
                            )}
                          </div>
                          {/* [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Mask name when isIdentified=false */}
                          <div className="text-[10px]">{getItemDisplayName(item)}</div>
                          <div className="text-[9px] opacity-75">
                            {(() => {
                              const itemWithMethods = Object.assign(Object.create(InventoryItem.prototype), item);
                              const w = itemWithMethods.getTotalWeight();
                              return (isNaN(w) ? 0 : w).toFixed(1);
                            })()}lb
                          </div>
                          {item.isContainer && (
                            <div className="text-[8px] opacity-75">
                              ({item.containedItems?.length || 0} items)
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="text-2xl">{item.icon}</div>
                          {/* [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Mask name when isIdentified=false */}
                          <div className="text-[10px]">{getItemDisplayName(item)}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* [MAR 2026]: Icon legend — only shows when attunement items exist */}
      {showItemDetails && items?.some(i => i.requiresAttunement || i.attuned) && (
        <div className="mt-2 flex gap-3 text-[10px] text-gray-500">
          <span>{'\uD83D\uDD2E'} Attuned</span>
          <span>{'\uD83D\uDD12'} Needs Attunement</span>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // [PERFORMANCE]: Custom comparison — only re-render when meaningful props change
  // Function props (handleMouseDown, handleContextMenu, etc.) excluded because they
  // don't affect visual output and may not be memoized by parent
  return (
    prevProps.gridId === nextProps.gridId &&
    prevProps.cols === nextProps.cols &&
    prevProps.rows === nextProps.rows &&
    prevProps.items === nextProps.items &&
    prevProps.title === nextProps.title &&
    prevProps.selectedItem?.id === nextProps.selectedItem?.id &&
    prevProps.selectedItemSource === nextProps.selectedItemSource &&
    prevProps.enableDragDrop === nextProps.enableDragDrop &&
    prevProps.showItemDetails === nextProps.showItemDetails
  );
});

export default GridComponent;
